import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { useApi } from 'app/shared/contexts/api';
import { useBuilding } from 'app/shared/contexts/building';
import { ContentResponse, TenantContentData } from 'app/data/types/content';
import { useIntl } from 'react-intl';

// Keyed by locale too: `lang` is part of the request, so without it a locale change replays the
// previous language from cache instead of refetching.
export const getContentQueryKey = (contentUuid: string, locale: string): QueryKey => ['content', contentUuid, locale];

export const useContentQuery = (contentUuid?: string) => {
  const intl = useIntl();
  const { building } = useBuilding();
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi() as unknown as {
    apiUrl: string;
    getAuthHeaders: () => Promise<{ Authorization: string }>;
    getDefaultHeaders: (buildingUuid?: string) => Record<string, string>;
  };

  const { data, isLoading, isSuccess, isError } = useQuery({
    queryKey: getContentQueryKey(contentUuid ?? '', intl.locale),
    queryFn: async ({ signal }): Promise<TenantContentData> => {
      const authHeaders = await getAuthHeaders();
      const defaultHeaders = getDefaultHeaders(building?.uuid);
      const headers = { ...authHeaders, ...defaultHeaders };
      const params = new URLSearchParams({ lang: intl.locale });
      const response = await apiRequest<ContentResponse, void, void>({
        url: `${apiUrl}/content/tenant/v3/buildings/${building?.uuid}/content/${contentUuid}?${params.toString()}`,
        method: HttpMethod.GET,
        headers,
        signal,
      });

      if (response.isEmpty()) {
        return undefined as unknown as TenantContentData;
      }

      return response.data.data.content as TenantContentData;
    },
    enabled: !!contentUuid && !!apiUrl && !!getAuthHeaders && !!getDefaultHeaders && !!building?.uuid,
    placeholderData: (prev) => prev,
  });

  return {
    data,
    isLoading,
    isSuccess,
    isError,
  };
};
